import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "@/utils/api";
import { FileText, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function CreditNote() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [siteFilter, setSiteFilter] = useState("all");
  const [siteOptions, setSiteOptions] = useState([]);

  useEffect(() => {
    apiGet("/lookups/sites").then((resp) => setSiteOptions(resp.items || [])).catch(() => null);
  }, []);

  useEffect(() => {
    // Pull real SAP-posted supplier credits (Credit Memo 005 + Subsequent Credit 006)
    // from the new supplier-credit route. The legacy /adjustments route is kept as a
    // fallback for round-off adjustments which continue to use that path.
    Promise.all([
      apiGet("/supplier-credits").then((r) => r.items || []).catch(() => []),
      apiGet("/adjustments?direction=subsequent_credit").then((r) => r.items || []).catch(() => []),
    ])
      .then(([credits, adjustments]) => {
        // Normalize both to the same shape for the table
        const normalized = [
          ...credits.map((c) => ({
            adjustment_id: c.credit_id,
            vendor_id: c.vendor_id,
            site: c.site,
            // Credit Memo via ReturnToSupplierIn exposes sap_rts_id instead of
            // sap_credit_id; show whichever SAP document is available so the
            // user can trace this credit end-to-end in SAP ByD.
            source_sap_invoice_id:
              c.parent_sap_invoice_id ||
              c.sap_credit_id ||
              (c.sap_rts_id ? `RTS ${c.sap_rts_id}` : ""),
            amount: c.grand_total,
            status: c.flow === "credit_memo" ? `CM · ${c.status}` : `SC · ${c.status}`,
          })),
          ...adjustments.map((a) => ({ ...a, status: `Round-off · ${a.status}` })),
        ];
        setNotes(normalized);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = notes.filter(
    (n) =>
      (siteFilter === "all" || n.site === siteFilter) &&
      (n.adjustment_id?.toLowerCase().includes(search.toLowerCase()) ||
      n.vendor_id?.toLowerCase().includes(search.toLowerCase()) ||
      n.source_sap_invoice_id?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-heading font-semibold text-slate-900">Credit Notes</h2>
          <p className="text-[11px] text-slate-400 font-mono mt-0.5">Record credit notes received from suppliers</p>
        </div>
        <Button
          onClick={() => navigate("/invoices/adjustment?direction=subsequent_credit")}
          className="gap-2 text-sm h-9"
          style={{ backgroundColor: "#0A6755", color: "#fff" }}
          data-testid="create-credit-note-btn"
        >
          <Plus size={14} /> New Credit Note
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search by vendor or note #"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
            data-testid="credit-note-search-input"
          />
        </div>
        <Select value={siteFilter} onValueChange={setSiteFilter}>
          <SelectTrigger className="w-32 h-9 text-sm border-slate-300" data-testid="credit-note-site-filter-select">
            <SelectValue placeholder="All sites" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sites</SelectItem>
            {siteOptions.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.id}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="hidden grid-cols-6 px-4 py-2.5 bg-slate-50 border-b border-slate-200 sm:grid">
          {["Credit Note #", "Vendor", "Site", "Reference Invoice", "Amount", "Status"].map((h) => (
            <span key={h} className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{h}</span>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-5 h-5 border-2 border-[#0A6755] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
              <FileText size={18} className="text-slate-400" />
            </div>
            <p className="text-[13px] text-slate-500" data-testid="empty-credit-notes-message">No credit notes found</p>
            <p className="text-[11px] text-slate-400">Create your first credit note</p>
          </div>
        ) : (
          filtered.map((n) => (
            <div key={n.adjustment_id} className="grid grid-cols-1 gap-1 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 sm:grid-cols-6 sm:gap-0" data-testid={`credit-note-row-${n.adjustment_id}`}>
              <span className="text-[13px] font-mono text-slate-900" data-testid={`credit-note-id-${n.adjustment_id}`}>{n.adjustment_id}</span>
              <span className="text-[13px] text-slate-700" data-testid={`credit-note-vendor-${n.adjustment_id}`}>{n.vendor_id}</span>
              <span className="text-[13px] font-mono text-slate-500" data-testid={`credit-note-site-${n.adjustment_id}`}>{n.site || "—"}</span>
              <span className="text-[13px] font-mono text-slate-500" data-testid={`credit-note-source-${n.adjustment_id}`}>{n.source_sap_invoice_id}</span>
              <span className="text-[13px] text-slate-900" data-testid={`credit-note-amount-${n.adjustment_id}`}>₹{Number(n.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 w-fit" data-testid={`credit-note-status-${n.adjustment_id}`}>{n.status}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
